/**
 * Topics at run time (D231) — which of a thing's topics answers a question,
 * what the player may ask about that is no thing, and the ledger of what has
 * been said once (`docs/design/actors.md` §4; `actors-content.md` §5.1/§5.3).
 *
 * @remarks
 * Three questions, one module, because they are one rule read from three
 * ends. **Which topic speaks:** the first in declared order whose keys match
 * the subject, whose `when` holds, and which is not spent. **What a key
 * matches:** a plain key is the `about` phrase as the player typed it —
 * folded as the parser folds input, compared whole, so `the film` and `film`
 * meet and `films` does not (the reaction table's discipline, D27: never
 * fuzzy); an id key is that item's or room's id, or any word the thing
 * answers to, so one token says "every name of the mask". **What the player
 * may ask about:** every plain key of every topic-bearing thing present —
 * handed to the parser by the topic catalog as topic-word entries
 * (`entityRefForTopicWord`), each marked `absent` like D226's met-elsewhere
 * things, so `ask about the directive` resolves where no item is called
 * that, and only as an `ask`'s subject (D226's adoption rule, unchanged).
 *
 * The ledger is the player's (Mike's ruling 4, 2026-10-04): a `once` topic,
 * spoken, is in `narrative.spentTopicIds` and the checkpoint keeps it with
 * the voice ledger. The world is put back; what was said stays said.
 *
 * @packageDocumentation
 */

import {
	type ActorId,
	isTopicIdKey,
	type ItemId,
	type NarrativeLine,
	type RoomId,
	type RuntimeState,
	type TopicDefinition,
} from '@orpheus/content-schema';
import { foldCharacters, type ParseCandidateEntity } from '@orpheus/parser-engine';

import type { AskParty, AskSubject } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { actorIdForEntityRef, itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';

/** The two topic fields, as items, rooms and (D232) actors declare them. */
interface TopicFields {
	readonly topicFallbackNarratives?: readonly NarrativeLine[];
	readonly topics?: readonly TopicDefinition[];
}

/** A party and its topic fields, whichever kind of thing it is. */
export interface TopicBearer extends TopicFields {
	readonly party: AskParty;
}

/** A bearer that declares a table — what {@link presentTopicBearers} returns. */
export type DeclaringTopicBearer = { readonly topics: readonly TopicDefinition[] } & TopicBearer;

/**
 * A party's topic fields, read off its definition.
 *
 * @param bundle - The loaded bundle
 * @param party - A thing or the room
 * @returns The bearer — `topics` is `undefined` for a thing that declares none
 */
export const topicBearerOf = (bundle: EngineGameBundle, party: AskParty): TopicBearer => {
	const definition: TopicFields | undefined = topicFieldsOf(bundle, party);

	return {
		party,
		...(definition?.topicFallbackNarratives === undefined ? {} : { topicFallbackNarratives: definition.topicFallbackNarratives }),
		...(definition?.topics === undefined ? {} : { topics: definition.topics }),
	};
};

/**
 * A party's definition, as far as its topic fields go — the one place the
 * three kinds are told apart.
 *
 * @param bundle - The loaded bundle
 * @param party - A thing, the room, or an actor
 * @returns The definition, or `undefined` when the bundle has none by that id
 */
const topicFieldsOf = (bundle: EngineGameBundle, party: AskParty): TopicFields | undefined => {
	switch (party.kind) {
		case 'actor': {
			return bundle.indexes.actorsById.get(party.actorId);
		}
		case 'item': {
			return bundle.indexes.itemsById.get(party.itemId);
		}
		case 'room': {
			return bundle.indexes.roomsById.get(party.roomId);
		}
	}
};

/**
 * Every word a party answers to, folded — its name, nouns and aliases — so
 * an id key stands for all of them.
 *
 * @param bundle - The loaded bundle
 * @param id - An item, room or actor id, as a topic key names it
 * @returns The folded words, or none when nothing is declared by that id
 */
const wordsAnsweredById = (bundle: EngineGameBundle, id: string): readonly string[] => {
	// A key's id is a string until the loader has said which family it names;
	// every lookup is tried, and a miss in all is an empty word list.
	const item = bundle.indexes.itemsById.get(id as ItemId);

	if (item !== undefined) {
		return [item.name, ...item.nouns, ...item.aliases].map(foldCharacters);
	}

	// D232: an actor answers to its words as an item does.
	const actor = bundle.indexes.actorsById.get(id as ActorId);

	if (actor !== undefined) {
		return [actor.name, ...actor.nouns, ...actor.aliases].map(foldCharacters);
	}

	const room = bundle.indexes.roomsById.get(id as RoomId);

	return room === undefined ? [] : [room.name, ...room.nouns].map(foldCharacters);
};

/**
 * The id of the entity a subject names, when it names one.
 *
 * @param subject - What was asked about
 * @returns The item, room or actor id, or `undefined` for a bare word
 */
const idOfSubject = (subject: AskSubject): string | undefined => {
	switch (subject.kind) {
		case 'actor': {
			return subject.actorId;
		}
		case 'item': {
			return subject.itemId;
		}
		case 'room': {
			return subject.roomId;
		}
		case 'word': {
			return undefined;
		}
	}
};

/**
 * The words the player used for the subject, folded: the surface the parser
 * kept, or the bare word itself.
 *
 * @param subject - What was asked about
 * @returns The folded surface, or `undefined` when the parser kept none
 */
const surfaceOfSubject = (subject: AskSubject): string | undefined =>
	subject.kind === 'word' ? foldCharacters(subject.word) : subject.surface === undefined ? undefined : foldCharacters(subject.surface);

/**
 * Does one topic key match the subject?
 *
 * @param bundle - The loaded bundle
 * @param key - One key of a topic, as authored
 * @param subject - What was asked about
 * @returns Whether the key answers to the subject
 */
const keyMatchesSubject = (bundle: EngineGameBundle, key: string, subject: AskSubject): boolean => {
	const surface = surfaceOfSubject(subject);

	if (isTopicIdKey(key)) {
		// The id itself (a pronoun resolved to the thing keeps no surface, and
		// still matches), or any word the thing answers to.
		return idOfSubject(subject) === key || (surface !== undefined && wordsAnsweredById(bundle, key).includes(surface));
	}

	return surface !== undefined && foldCharacters(key) === surface;
};

/**
 * The topic that answers this subject NOW: first in declared order whose keys
 * match, whose `when` holds, and which is not already spent.
 *
 * @param bundle - The loaded bundle
 * @param state - The live state (conditions and the ledger read it)
 * @param topics - A thing's topic table
 * @param subject - What was asked about
 * @returns The topic, or `undefined` when none answers
 */
export const liveTopicFor = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	topics: readonly TopicDefinition[],
	subject: AskSubject,
): TopicDefinition | undefined =>
	topics.find(
		(topic) =>
			topic.keys.some((key) => keyMatchesSubject(bundle, key, subject)) &&
			!(topic.once && state.narrative.spentTopicIds.includes(topic.topicId)) &&
			(topic.when === undefined || evaluateCondition(topic.when, bundle, state)),
	);

/**
 * The state with a `once` topic spent — the player's ledger grows by one. A
 * topic that is not `once`, or is already in the ledger, leaves the state as
 * it was.
 *
 * @param state - The state before the answer
 * @param topic - The topic about to speak
 * @returns The state with the topic recorded as said
 */
export const withTopicSpent = (state: RuntimeState, topic: TopicDefinition): RuntimeState =>
	!topic.once || state.narrative.spentTopicIds.includes(topic.topicId)
		? state
		: { ...state, narrative: { ...state.narrative, spentTopicIds: [...state.narrative.spentTopicIds, topic.topicId]}};

/**
 * The plain keys of every topic a bearer declares, folded, each once.
 *
 * @param bearer - A topic-bearing thing
 * @returns The words, in declared order
 */
const plainKeyWordsOf = (bearer: DeclaringTopicBearer): readonly string[] => [
	...new Set(bearer.topics.flatMap((topic) => topic.keys.filter((key) => !isTopicIdKey(key)).map(foldCharacters))),
];

/**
 * The topic-bearing parties among a catalog's entries, in catalog order —
 * things first, the room last (where you stand is the least particular
 * speaker). What is referable HERE is what may be asked, so the one catalog
 * derivation decides presence for the parser and the handler alike.
 *
 * @param bundle - The loaded bundle
 * @param presentCatalog - This turn's catalog
 * @returns The bearers that declare topics
 */
export const presentTopicBearers = (bundle: EngineGameBundle, presentCatalog: readonly ParseCandidateEntity[]): readonly DeclaringTopicBearer[] => {
	const things: TopicBearer[] = [];
	const rooms: TopicBearer[] = [];

	for (const entry of presentCatalog) {
		const roomId = roomIdForEntityRef(entry.entityRef);
		const actorId = actorIdForEntityRef(entry.entityRef);

		if (roomId !== undefined) {
			rooms.push(topicBearerOf(bundle, { kind: 'room', roomId }));
		} else if (actorId !== undefined) {
			// D232: a body in the room knows what it knows, in catalog order with the things.
			things.push(topicBearerOf(bundle, { actorId, kind: 'actor' }));
		} else if (bundle.indexes.itemsById.has(itemIdForEntityRef(entry.entityRef))) {
			things.push(topicBearerOf(bundle, { itemId: itemIdForEntityRef(entry.entityRef), kind: 'item' }));
		}
	}

	return [...things, ...rooms].filter((bearer): bearer is DeclaringTopicBearer => bearer.topics !== undefined);
};

/**
 * The topic WORDS the player may ask about here: every plain key of every
 * present bearer's topics that nothing HERE already answers to — a key a
 * present thing is named by resolves to the thing, and the thing's surface
 * meets the key in {@link liveTopicFor}; two entries for one word would be a
 * which-one question about a word. Each word once, in bearer order; the
 * topic catalog makes them entries.
 *
 * D245 (the Meridian play-test's A7): a thing met ELSEWHERE no longer takes
 * the word. `ask marrow about the drum` in the lobby answered "You see no
 * drum here" though `drum` is a key of her lift topic: three things in rooms
 * the player had walked answer to "drum", the absent tie is no question
 * worth asking (D226), and the word stood aside for them. The key is the
 * answer somebody here HAS, so it outranks the things that are not here
 * (`SALIENCE_TOPIC_WORD`), and an id key still meets the word through the
 * thing's own names.
 *
 * @param bundle - The loaded bundle
 * @param presentCatalog - This turn's catalog — the bearers are what is HERE
 * @returns The words, folded
 */
export const topicWordsAskableHere = (bundle: EngineGameBundle, presentCatalog: readonly ParseCandidateEntity[]): readonly string[] => {
	const knownNames = new Set(presentCatalog.flatMap((entry) => [...entry.nouns, ...entry.aliases].map(foldCharacters)));
	const words: string[] = [];

	for (const bearer of presentTopicBearers(bundle, presentCatalog)) {
		for (const word of plainKeyWordsOf(bearer)) {
			if (!knownNames.has(word) && !words.includes(word)) {
				words.push(word);
			}
		}
	}

	return words;
};
